'use strict';

import React, {
    useMemo
} from 'react';
import {useSelector} from 'react-redux';
import {keys} from 'ts-transformer-keys';

import {
    __,
    Placeholders
} from '~/shared/src/utilities/messageCatalog';
import {LoadDataParameters} from '~/shared/src/utilities/modules/chart';

import Chart from '~/frontend/src/components/base/Chart';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';

import {StoreState} from '~/frontend/src/store';
import {
    Alignment,
    Visibility
} from '~/shared/src/utilities/modules/form';
import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import {MessageResolver} from '~/frontend/src/components/base/shared/field/FieldWrapper';
import {getSubPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';


type DefaultProps = {

    /**
     * The CSS class name(s) to add to the wrapper.
     */
    classNames? : string[];

    /**
     * Is he chart disabled?
     */
    disabled? : boolean;

    /**
     * How the view to display as?
     */
    displayMode? : 'card' | 'field';

    /**
     * The number of text rows to be displayed within the chart.
     */
    height? : number;

    /**
     * Should the chart marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The max number of text rows to be displayed within the chart.
     * 0 means unlimited
     */
    maxHeight? : number;

    /**
     * The styles to style the wrapper with.
     */
    style? : React.CSSProperties;

    /**
     * The alignment of the title for the chart.
     */
    titleAlignment? : Alignment;

    /**
     * The CSS class names to title the label with.
     */
    titleClassNames? : string[];

    /**
     * The styles to style the title with.
     */
    titleStyle? : React.CSSProperties;

    /**
     * The visibility of the title for the chart.
     */
    titleVisibility? : Visibility;

    /**
     * The visibility of the field
     */
    visibility? : Visibility;

};
const defaultProps : Required<DefaultProps> = {
    classNames     : [ 'yn-field-wrapper', 'field' ],
    disabled       : false,
    displayMode    : 'field',
    height         : 0,
    markDirty      : false,
    maxHeight      : 0,
    style          : {},
    titleAlignment : 'left',
    titleClassNames: [ 'yn-field-label' ],
    titleStyle     : {},
    titleVisibility: 'visible',
    visibility     : 'visible'
};

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    id? : string;

    viewKey : string;

    dataContext : string;

    /**
     * Additional parameters for the query.
     *
     * NOTE: A mapping between query fields and form contents.
     *
     * USAGE:
     * DataStructure based query:
     *   - key: ${<DataObjectOfQuery.itemKey>.<attributePath>}; value: ${<DataObjectOfForm.itemKey>.<attributePath>}
     * Cypher or SQL based query:
     *   - key: ${PlaceholderAsWrittenInQuery}; value: ${<DataObjectOfForm.itemKey>.<attributePath>}
     */
    additionalParameters? : Required<LoadDataParameters>[ 'parameters' ];

    /**
     * The placeholders for messages translated by MessageCatalog.
     */
    messagePlaceholders? : Placeholders;

    /**
     * A resolver function for message templateStrings.
     *
     * @param templateString
     */
    messageTemplateStringResolver? : MessageResolver;

    /**
     * The title for the chart.
     */
    title? : string;

    /**
     * PassTrough options
     */
    pt? : {
        root? : React.HTMLAttributes<HTMLDivElement> & { [ key : `data-${string}` ] : string };
    };
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


const ChartView = ( props : AllProps ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        const {
                  additionalParameters,
                  classNames,
                  dataContext,
                  disabled,
                  displayMode,
                  height,
                  id,
                  markDirty,
                  maxHeight,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  pt,
                  style,
                  title,
                  titleAlignment,
                  titleClassNames,
                  titleStyle,
                  titleVisibility,
                  uid,
                  viewKey
              }               = props,
              elementId       = id ?? `${uid}_chartView`,
              chartUid        = `${uid}_chart`,
              user            = useSelector( ( state : StoreState ) => state.session?.user ),
              chartProps      = useSelector( ( state : StoreState ) => {
                  let retVal : PanelProps | undefined = getSubPanelProps( chartUid, state );

                  if ( Object.keys( retVal ).length === 0 ) {
                      retVal = undefined;
                  }

                  return retVal;
              } ),
              messageResolver = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      messagePlaceholders,
                      messageTemplateStringResolver
                  ]
              ),
              resolvedTitle   = useMemo(
                  () => {
                      return title ? messageResolver( title ) : undefined;
                  },
                  [
                      JSON.stringify( title ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              heightStyle     = useMemo(
                  () => {
                      const retVal = getHeightStyle( {}, height, maxHeight, 'hidden', 'left', 'hidden' );

                      retVal.minHeight = '20rem';

                      return retVal;
                  },
                  [
                      height,
                      maxHeight
                  ]
              ),
              chart           = useMemo(
                  () => {
                      return <Chart
                          uid={chartUid}
                          dataContext={dataContext}
                          mode="embedded"
                          viewKey={viewKey}
                          additionalQueryParameters={additionalParameters}
                          {...chartProps}
                      />;
                  },
                  [
                      additionalParameters,
                      chartProps,
                      chartUid,
                      dataContext,
                      viewKey
                  ]
              );


        // TODO: non-autoHeight (height = 0) not working (e.g. height = 2)


        return useMemo(
            () => {
                return <div
                    {...pt?.root}
                    id={elementId}
                    key={elementId}
                    className="yn-form-view yn-form-view-chart"
                    style={style}
                >
                    <LoadingMaskPanel
                        key={`${chartUid}_loadingMask`}
                        forComponent={chartUid}
                        className="yn-view-loadingmask"
                        style={{ width: '100%' }}
                    >
                        <div
                            className={`yn-view-wrapper yn-view-wrapper-chart ${classNames.join( ' ' )}`}
                            style={{ ...heightStyle }}
                        >
                            {
                                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                                ( title || titleVisibility === 'placeholder' ) &&
                                <div className={`yn-form-container-title yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility} ${titleClassNames.join( ' ' )}`} style={titleStyle}>{resolvedTitle ?? ' '}</div>
                            }
                            {chart}
                        </div>
                    </LoadingMaskPanel>
                </div>;
            },
            [
                chart,
                chartUid,
                classNames,
                elementId,
                heightStyle,
                pt,
                resolvedTitle,
                style,
                title,
                titleAlignment,
                titleClassNames,
                titleStyle,
                titleVisibility
            ]
        );
    }
};
ChartView.defaultProps = defaultProps;
ChartView.displayName = 'ChartView';

export default ChartView;
